import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import Tabs from '@/common/components/Tabs'
import { useCompanyBranch } from '@/common/hooks/queries/useCompanyBranch'
import { cn } from '@/common/utils/cn'
import { dateTransformer } from '@/common/utils/dateTransformer'
import { formatCurrency } from '@/common/utils/formatters'
import { useMineCompany } from '@/features/company/hooks/useMineCompany'
import { CompanyContractConsumptionControlledBy } from '@/features/company/types/contract.type'
import { CreateGovernmentAllocationAnnulativeEntryModal } from '@/features/governmentAllocations/components/CreateGovernmentAllocationAnnulativeEntryModal'
import { GovernmentAllocationOptions } from '@/features/governmentAllocations/components/GovernmentAllocationOptions'
import { useGovernmentAllocation } from '@/features/governmentAllocations/hooks/useGovernmentAllocation'
import { GovernmentAllocationDistribution } from '@/features/governmentAllocations/pages/Show/components/GovernmentAllocationDistribution'
import { GovernmentAllocationDocuments } from '@/features/governmentAllocations/pages/Show/components/GovernmentAllocationDocuments'
import { GovernmentAllocationInfoItem } from '@/features/governmentAllocations/pages/Show/components/GovernmentAllocationInfoItem'
import { GovernmentAllocationSummary } from '@/features/governmentAllocations/pages/Show/components/GovernmentAllocationSummary'
import { governmentAllocationStatusStrategy } from '@/features/governmentAllocations/pages/Show/constants/governmentAllocationStatus.constant'
import { isAfter, isWithinInterval } from 'date-fns'
import { ChevronLeftIcon, ChevronsRightIcon, FileCheckIcon, FileClockIcon, OctagonAlertIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ShowGovernmentAllocationLoadingState from './components/ShowGovernmentAllocationLoadingState'
import { GovernmentAllocationDistributionsLimits } from '../../components/GovernmentAllocationDistributionsLimits'
import { useCurrentUser } from '@/common/hooks/useCurrentUser'

const Show = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: company, isPending: companyIsPending } = useMineCompany()

  const { data: currentUser } = useCurrentUser({});

  const {
    data: governmentAllocation,
    isPending: governmentAllocationIsPending,
    refetch: refetchGovernmentAllocation,
    error: errorGovernmentAllocation = {} as any,
  } = useGovernmentAllocation(id || '')

  const { data: branchData, isPending: branchDataIsPending } = useCompanyBranch(
    governmentAllocation?.branch_id || '',
    !!governmentAllocation?.branch_id,
  )

  const isInPeriod = useMemo(() => {
    return governmentAllocation
      ? isWithinInterval(new Date(), {
        start: new Date(governmentAllocation?.applicable_start_date),
        end: new Date(governmentAllocation?.applicable_end_date),
      })
      : false
  }, [governmentAllocation?.applicable_start_date, governmentAllocation?.applicable_end_date])

  const isExpired = useMemo(() => {
    return governmentAllocation ? isAfter(new Date(), new Date(governmentAllocation?.applicable_end_date)) : false
  }, [governmentAllocation?.applicable_end_date])

  const isConsumptionControlledByQuantity =
    governmentAllocation?.controlled_by === CompanyContractConsumptionControlledBy.Quantity

  if (governmentAllocationIsPending || companyIsPending || (branchDataIsPending && governmentAllocation?.branch_id)) {
    return <ShowGovernmentAllocationLoadingState />
  }

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={errorGovernmentAllocation?.status}
      refetchFunction={refetchGovernmentAllocation}
    >
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-4 items-center">
          <Button variant="link-white" onClick={() => navigate('/government-allocations')}>
            <ChevronLeftIcon size={40} strokeWidth={1} />
          </Button>

          <div className="flex items-center gap-1">
            <p className="text-white-400">{company?.matrix_branch?.general_information?.fantasy_name}</p>

            {branchData?.display_name && (
              <>
                <ChevronsRightIcon className="text-white-400" />
                <p className="text-white-800">{branchData.display_name}</p>
              </>
            )}

            {governmentAllocation && (
              <>
                <ChevronsRightIcon className="text-white-400" />
                <p className="text-white-800">{governmentAllocation.main_entry.office_number}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <Box className="flex flex-col gap-6 h-[calc(100vh-128px)] overflow-y-auto">
        {governmentAllocation && (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-end gap-2">
                <p className="text-lg font-bold leading-5">Empenho</p>

                {!governmentAllocation.simplified_government_allocation_view && (
                  <Link to={`/government-allocations/${governmentAllocation?.main_entry.id}/edit`}>
                    <Button variant="link">Ver contrato</Button>
                  </Link>
                )}

                <div className="flex items-center gap-2">
                  {isExpired ? (
                    <FileClockIcon strokeWidth={1} className="size-6 text-danger" />
                  ) : (
                    <FileCheckIcon strokeWidth={1} className="size-6 text-success" />
                  )}
                  <p className={cn('text-xs font-bold leading-3', isExpired ? 'text-danger-soft' : 'text-valid')}>
                    {isExpired ? 'Expirado' : 'Vigente'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <GovernmentAllocationOptions />

                {isExpired && (
                  <CreateGovernmentAllocationAnnulativeEntryModal governmentAllocation={governmentAllocation} />
                )}
              </div>
            </div>
            <div className="flex items-start gap-6">
              <GovernmentAllocationInfoItem
                label="Status"
                value={
                  governmentAllocationStatusStrategy[
                  governmentAllocation.status as keyof typeof governmentAllocationStatusStrategy
                  ]
                }
              />

              <GovernmentAllocationInfoItem label="Nº Empenho" value={governmentAllocation.main_entry.office_number} />

              <GovernmentAllocationInfoItem
                label="Período de competência"
                value={`${dateTransformer(governmentAllocation.applicable_start_date)} à ${dateTransformer(
                  governmentAllocation.applicable_end_date,
                )}`}
              />

              <GovernmentAllocationInfoItem
                label="Tipo de controle de empenho"
                value={
                  company?.current_contract?.consumption_controlled_by === 'currency'
                    ? 'Por valor em R$'
                    : 'Por quantidade'
                }
              />
            </div>

            {governmentAllocation?.controlled_by === 'currency' && (
              <div className="flex items-start gap-6">
                <GovernmentAllocationInfoItem
                  label="Valor total empenhado"
                  value={formatCurrency(Number(governmentAllocation.total_value))}
                />

                <GovernmentAllocationInfoItem
                  label="Valor total aprovado"
                  value={formatCurrency(Number(governmentAllocation.approved_value))}
                />

                <GovernmentAllocationInfoItem
                  label="Restante a aprovar"
                  value={formatCurrency(
                    Number(governmentAllocation.total_value) - Number(governmentAllocation.approved_value),
                  )}
                />

                <GovernmentAllocationInfoItem
                  label="Total distribuido"
                  value={formatCurrency(Number(governmentAllocation.distributed_total))}
                />

                <GovernmentAllocationInfoItem
                  label="Restante a distribuir"
                  value={formatCurrency(Number(governmentAllocation.available_to_distribution))}
                />

                <GovernmentAllocationInfoItem
                  label="Valor total consumido"
                  value={formatCurrency(Number(governmentAllocation.consumed_total))}
                  className="text-yellow-400"
                />

                <div className="flex items-start gap-2">
                  {isExpired && <OctagonAlertIcon strokeWidth={2} className="size-5" color="#FBBCB6" />}

                  <GovernmentAllocationInfoItem
                    label={isExpired ? 'Valor total bloqueado' : 'Valor total disponível'}
                    value={formatCurrency(Number(governmentAllocation.remaining_total))}
                    className={cn(isExpired ? 'text-danger-soft' : 'text-valid')}
                  />
                </div>
              </div>
            )}

            <Tabs
              tabsListClassName="w-full bg-transparent rounded-none"
              tabsTriggerClassName="h-8 w-full rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800"
              tabsContentClassName="mt-6"
              tabsList={[
                {
                  label: 'Resumo de Distribuições',
                  value: '',
                  body: <GovernmentAllocationSummary governmentAllocation={governmentAllocation} />,
                },
                {
                  label: 'Documentos de empenho',
                  value: 'documents',
                  body: (
                    <GovernmentAllocationDocuments
                      governmentAllocation={governmentAllocation}
                      showReinforcementButton={!isExpired}
                      disableReinforcement={!isInPeriod}
                    />
                  ),
                  hidden: governmentAllocation.simplified_government_allocation_view,
                },
                {
                  label: 'Divisão de crédito do empenho',
                  value: 'distribution',
                  operationKlassToCheckPermission: 'Client::Billing::GovernmentAllocations::DistributionsFilter',
                  body: (
                    <GovernmentAllocationDistribution
                      isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
                    />
                  ),
                },
                {
                  label: 'Limites de distribuição do empenho',
                  value: 'distribution-limits',
                  operationKlassToCheckPermission: 'Client::Billing::GovernmentAllocations::DistributionsLimits',
                  body: <GovernmentAllocationDistributionsLimits controlledBy={governmentAllocation.controlled_by} />,
                  hidden: governmentAllocation?.branch_id === currentUser?.branch?.id,
                },
              ]}
            />
          </>
        )}
      </Box>
    </ErrorMessageWrapper>
  )
}

export default Show
